import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Plus, Trash2 } from 'lucide-react';
import Title from '../../components/Title';
import List from '../../components/List';
import Line from '../../components/Line';
import Actions from '../../components/Actions';
import Header from '../../components/Header';
import Tabs from '../../components/Tabs';
import { nameWithDefault, itemsHaveContent } from '../../utils/helpers';
import { STRATEGIES_MAX_COUNT } from '../../utils/const';
import Button from '../../components/Button';
import Textarea from '../../components/Textarea';
import TabsCut from '../../components/TabsCut';
import TabsList from '../../components/TabsList';
import TabsListTitle from '../../components/TabsListTitle';
import TabsListItem from '../../components/TabsListItem';
import Links from '../../components/Links';
import ConfirmationModal from '../../components/ConfirmationModal';
import SvgIcon from '../../components/SvgIcon';
import MobileMenuHeader from '../../components/MobileMenuHeader';

const csFields = [
  'name',
  'description',
  'frustrates',
  'goals',
  'problems',
  'type',
  'image',
];

const fields = ['name', 'description'];
const collectionName = 'growStrategies';
const icons = ['complexArrow', 'text'];
const iconsComponents = icons.map((e) => <SvgIcon name={e} />);

export default function Grow(props) {
  const {
    nextClickHandler,
    menuItems,
    selectComponent,
    componentIndex,
    rules,
    backPath,
    locales,
    locales: {
      caseStudies,
      common,
      tipCustomerSegment,
      getKeepGrow: {
        name,
        grow: { header, title, text, list, tabs, customerSegmentsTitle },
      },
    },
  } = props;
  const customerSegments = useSelector(
    (state) => state.customerSegment.collection,
  );
  const { growStrategies } = useSelector((state) => state.getKeepGrow);
  const {
    getKeepGrow: { addStrategy, updateStrategy, removeStrategy },
  } = useDispatch();
  const [opened, setOpened] = useState(false);

  return (
    <section className="bmc-page" aria-label={header}>
      <Header
        title={header}
        subtitle={name}
        icon={<SvgIcon name="gkg" />}
        locales={locales}
        componentIndex={componentIndex}
        tipLocales={tipCustomerSegment}
        menuItems={menuItems}
        onMenuItemClick={selectComponent}
        rules={rules}
        backPath={backPath}
        mobileMenuHeader={
          <MobileMenuHeader icon="CRCanvas" title={name} color="green" />
        }
      />

      <div className="bmc-page-text bmc-page-text--center">
        <p>
          <span className="bmc-page-text--bold">{text[0]}</span>
          {text[1]}
        </p>
      </div>

      <Title text={title[0]} />

      <List items={list} type="horizontal" icons={icons} />

      <Line />

      <h2 className="bmc-page-title">
        <span className="bmc-page-title--green">{title[1]}</span> {title[2]}
      </h2>

      <div className="bmc-page-text bmc-page-text--center">
        <p>
          <span className="bmc-page-text--bold">{text[2]}</span>
          {text[3]}
        </p>
      </div>

      {itemsHaveContent(customerSegments, csFields) && (
        <div className="margin-top-40">
          <h4 className="font-bold text-md w-full text-center">
            {customerSegmentsTitle}
          </h4>
        </div>
      )}

      {itemsHaveContent(customerSegments, csFields) && (
        <Links items={customerSegments} />
      )}

      <Tabs
        tabs={[common.yourStrategy, common.caseStudy]}
        withBlur
        className="margin-top-40"
      >
        {[
          <div key="strategy">
            {growStrategies.map((strategy, index) => (
              <TabsList
                key={index}
                formStyle
                divider={index < STRATEGIES_MAX_COUNT - 1}
              >
                <TabsListTitle className="flex flex-row justify-between items-center">
                  {nameWithDefault(strategy.name, name, index + 1)}

                  {growStrategies.length > 1 && (
                    <ConfirmationModal
                      className="ml-3 hover:bg-muted-lighter/60 text-muted"
                      body={locales.common.deleteConfirmation}
                      confirmText="Delete"
                      isBlockScroll
                      onConfirm={() =>
                        removeStrategy({ index, collectionName })
                      }
                    >
                      <Trash2 className="w-6 min-w-6 h-auto" />
                    </ConfirmationModal>
                  )}
                </TabsListTitle>

                {fields.map((field, i) => (
                  <TabsListItem
                    divider={false}
                    className={`${i !== fields.length - 1 && 'mb-10'}`}
                    key={i}
                    title={tabs[field]}
                    subtitle={tabs[`${field}Sub`]}
                    icon={iconsComponents[i]}
                    text={
                      // eslint-disable-next-line
                      <Textarea
                        delay={700}
                        placeholder={tabs[field]}
                        value={strategy[field]}
                        onChange={(event) =>
                          updateStrategy({
                            name: field,
                            value: event.target.value,
                            index,
                            collectionName,
                          })
                        }
                      />
                    }
                  />
                ))}
              </TabsList>
            ))}

            {growStrategies.length < STRATEGIES_MAX_COUNT && (
              <div className="bmc-page-tabs-content-actions margin-top-40">
                <Button
                  variant="outline"
                  onClick={() => addStrategy({ collectionName })}
                  className="bg-transparent hover:bg-secondary/50 border-secondary/50 text-secondary hover:text-secondary hover:border-secondary w-full"
                >
                  <Plus className="w-7 h-auto" />
                  {tabs.addNewStrategy}
                </Button>
              </div>
            )}
          </div>,

          <Tabs
            key="tabs"
            tabs={caseStudies.map((e) => e.name)}
            className="bmc-page-tabs--black bmc-page-tabs--inner"
          >
            {caseStudies.map((item, index) => (
              <div key={index}>
                <TabsCut
                  setOpened={setOpened}
                  opened={opened}
                  caseStudy={item}
                />

                {item.growStrategies.map((strategy, i) => (
                  <TabsList
                    key={i}
                    formStyle
                    preview
                    divider={i < item.getStrategies.length - 1}
                  >
                    <TabsListTitle>
                      {tabs.name} {i + 1}
                    </TabsListTitle>

                    {fields.map((field, ii) => (
                      <TabsListItem
                        divider={false}
                        className={`${ii < fields.length - 1 && 'mb-10'}`}
                        key={ii}
                        title={tabs[field]}
                        text={strategy[field]}
                        icon={iconsComponents[ii]}
                      />
                    ))}
                  </TabsList>
                ))}
              </div>
            ))}
          </Tabs>,
        ]}
      </Tabs>

      <Actions nextClickHandler={nextClickHandler} buttonName={common.next} />
    </section>
  );
}
